import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/lib/auth/auth-context";
import { canManageBackOffice } from "@/lib/auth/roles";
import { cn } from "@/lib/utils/cn";

// The partner-provisioning UI lives in the partners slice; render it here under
// Administration (matching the old app, which exposes provisioning under the
// Administration tab — not the owner-facing "Groupe partenaire" area kept hidden
// from plain admins per B-3). Side-effect import registers its i18n keys.
import "@/features/partners/i18n";
import { MemberDataView } from "@/features/partners/components/MemberDataView";
import { SuperUserManagement } from "@/features/partners/components/SuperUserManagement";
import { PromoCodeManagement } from "@/features/partners/components/PromoCodeManagement";

import { AssessmentView } from "./AssessmentView";
import { EnterpriseListView } from "./EnterpriseListView";
import { ProgressView } from "./ProgressView";

// "Enterprises" is the legacy single list-and-search view: one filter set over
// group / sector / MRC *and* questionnaire answers (issues, goals, actions, answer
// type). It was briefly split into a separate "Search" tab; that was redundant
// (same endpoint, same table) so it's folded back in. "partners" (admin-only)
// is the partner-provisioning surface (G-5 — otherwise unreachable via nav).
const BASE_TABS = ["enterprises", "progress", "assessment"] as const;
type Tab = (typeof BASE_TABS)[number] | "partners" | "promoCodes";

/** One enterprise being consulted on its own behalf, from the reports list. */
interface ViewingMember {
  profileId: string;
  name: string;
  tab: "diagnostic" | "plan";
}

/** Reports area shell: client-side tabs (the route tree is owned upstream). */
export function ReportsApp() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>("enterprises");
  const [member, setMember] = useState<ViewingMember | null>(null);
  // "partners" and "promoCodes" are admin-only back-office surfaces; they live
  // here because Administration is where admins already navigate (G-5).
  const tabs: Tab[] = canManageBackOffice(user)
    ? [...BASE_TABS, "partners", "promoCodes"]
    : [...BASE_TABS];

  // Consulting one enterprise takes over the area, as it does under
  // "Groupe partenaire" — the on-behalf context applies to the whole view, so
  // leaving the report tabs alongside it would invite reading one as the other.
  if (member) {
    return (
      <MemberDataView
        profileId={member.profileId}
        memberName={member.name}
        initialTab={member.tab}
        onBack={() => setMember(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">{t("reports.title")}</h1>
      </div>

      <div role="tablist" className="flex flex-wrap gap-1 border-b">
        {tabs.map((key) => (
          <button
            key={key}
            role="tab"
            type="button"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={cn(
              "min-h-touch border-b-2 px-4 py-2 text-sm font-medium transition-colors",
              tab === key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t(`reports.tabs.${key}`)}
          </button>
        ))}
      </div>

      {tab === "enterprises" ? (
        <EnterpriseListView
          onViewMember={(profileId, name, viewTab) =>
            setMember({ profileId, name, tab: viewTab })
          }
        />
      ) : null}
      {tab === "progress" ? <ProgressView /> : null}
      {tab === "assessment" ? <AssessmentView /> : null}
      {tab === "partners" ? <SuperUserManagement /> : null}
      {tab === "promoCodes" ? <PromoCodeManagement /> : null}
    </div>
  );
}
